import { FieldValues } from 'react-hook-form';
import useApiService from '../utilities/useApiService';
import { formatNdc } from '../utilities/ndc/ndcUtils';
import { useMutation, UseMutationResult } from 'react-query';
import { MutationHandlers } from '../network/models/MutationHandlers';
import { AxiosResponse } from 'axios';

interface MutationVariables {
   itemId: string;
   itemData: FieldValues;
}

export const useUpdateRequestItem = (
   pharmacyId: string,
   returnRequestId: string,
   handlers?: MutationHandlers<
      MutationVariables,
      AxiosResponse<any> | undefined
   >
): [
   any | undefined,
   (
      itemId: string,
      itemData: FieldValues,
      handlers?: Omit<
         MutationHandlers<MutationVariables, AxiosResponse<any> | undefined>,
         'onMutate'
      >
   ) => void,
   Omit<UseMutationResult<any, unknown, MutationVariables>, 'data' | 'mutate'>
] => {
   const { putMethod } = useApiService();

   const { data, mutate, ...state } = useMutation({
      mutationKey: [
         `PUT:/pharmacies/${pharmacyId}/returnrequests/${returnRequestId}/items/{itemId}`,
      ],
      mutationFn: async ({ itemId, itemData }: MutationVariables) => {
         if (!itemId) return undefined;
         if (!itemData) return undefined;

         const formattedNdc = formatNdc(itemData.ndc);
         return putMethod(
            `/pharmacies/${pharmacyId}/returnrequests/${returnRequestId}/items/${itemId}`,
            {
               ndc: formattedNdc,
               description: itemData.description,
               name: 'name',
               manufacturer: itemData.manufacturer,
               packageSize: itemData.pkgSize,
               gtin14: itemData.gtin14,
               serialNumber: itemData.serialNumber,
               controlledSubstanceCode: itemData.controlledSubstanceCode,
               requestType: 'typeofrequest',
               fullQuantity:
                  itemData.quantityType !== 'partial' ? itemData.quantity : '0',
               partialQuantity:
                  itemData.quantityType === 'partial' ? itemData.quantity : '0',
               strength: itemData.strength,
               dosage: itemData.dosage,
               status: 'PENDING',
               expirationDate: itemData.expDate,
               lotNumber: itemData.lot,
               isFullQuantityOpened: itemData.quantityType === 'fullOpened',
            }
         );
      },
      onError: handlers?.onError,
      onSuccess: handlers?.onSuccess,
      onSettled: handlers?.onSettled,
      onMutate: handlers?.onMutate,
   });

   return [
      data,
      (
         itemId: string,
         itemData: FieldValues,
         handlers?: Omit<
            MutationHandlers<MutationVariables, AxiosResponse<any> | undefined>,
            'onMutate'
         >
      ) => {
         mutate(
            { itemId, itemData },
            {
               onError: handlers?.onError,
               onSuccess: handlers?.onSuccess,
               onSettled: handlers?.onSettled,
            }
         );
      },
      state,
   ];
};
